import { Link } from 'react-router-dom';
import { Laptop, MapPin, MessageCircle, Monitor, PcCase, Server, Truck, type LucideIcon } from 'lucide-react';
import data from '@/data/recent-sales.json';

/**
 * "נמכר לאחרונה" — real computers sold through the lab (owner request
 * 2026-10-27: make computer sales the strongest, most visible part of the
 * site, and show that people buy and that it ships nationwide).
 *
 * Data: src/data/recent-sales.json — only sales the owner marked; buyer is a
 * first name (+ initial) and a town; price is what the customer paid, incl.
 * VAT (the one place products carry a price — FACTS.md exception). Dates are
 * printed as dd/mm, never "this week", so the prerendered HTML can't go stale.
 * Shipping line is FACTS.md verbatim: UPS nationwide, free over ₪2,000.
 * New sales arrive through an approve-first Telegram card
 * (~/.openclaw/workspace/automation/sales_feed.py), which writes the JSON.
 */
type Sale = (typeof data.sales)[number];

const KIND: Record<string, { label: string; Icon: LucideIcon }> = {
  laptop: { label: 'מחשב נייד', Icon: Laptop },
  desktop: { label: 'מחשב נייח', Icon: PcCase },
  aio: { label: 'מחשב All-in-One', Icon: Monitor },
  workstation: { label: 'תחנת עבודה', Icon: Server },
  mini: { label: 'מיני PC', Icon: PcCase },
};

const WA = `https://wa.me/972546672863?text=${encodeURIComponent('היי בניהו, ראיתי באתר מה נמכר לאחרונה. אני מחפש/ת מחשב ל')}`;

const ddmm = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${Number(d)}/${Number(m)}`;
};
const shekel = (n: number) => `₪${n.toLocaleString('en-US')}`;

function SaleCard({ s }: { s: Sale }) {
  const k = KIND[s.kind] ?? KIND.laptop;
  return (
    <article className="flex h-full flex-col rounded-2xl bg-white p-5 text-[var(--cin-ink)] shadow-[0_1px_0_rgba(15,23,41,0.04),0_12px_32px_-18px_rgba(15,23,41,0.45)] md:p-6">
      <div className="mb-4">
        <div className="mb-2 flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2 text-[13px] font-medium text-[var(--cin-muted)]">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--cin-blue)]/10 text-[var(--cin-blue)]" aria-hidden="true">
              <k.Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </span>
            {k.label}
          </span>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-600/20">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
            נמכר <span dir="ltr">{ddmm(s.date)}</span>
          </span>
        </div>
        <h3 className="font-heebo text-lg font-bold leading-snug tracking-normal [overflow-wrap:anywhere]">
          {s.model}
        </h3>
      </div>

      <ul className="mb-3 flex flex-wrap gap-1.5" aria-label="מפרט">
        {s.specs.map((spec) => (
          <li key={spec} dir="auto" className="rounded-md border border-[var(--cin-line)] bg-[var(--cin-paper-soft)] px-2 py-1 text-[13px] font-medium leading-none">
            {spec}
          </li>
        ))}
      </ul>
      {s.note && <p className="mb-4 text-sm leading-relaxed text-[var(--cin-muted)]">{s.note}</p>}

      <div className="mt-auto flex items-end justify-between gap-3 border-t border-dashed border-[var(--cin-line)] pt-4">
        <div className="flex min-w-0 items-center gap-1.5 text-sm">
          <MapPin className="h-4 w-4 shrink-0 text-[var(--cin-muted)]" aria-hidden="true" />
          <span className="truncate">
            <span className="font-bold">{s.buyer}</span>
            <span className="text-[var(--cin-muted)]"> · {s.place}</span>
          </span>
        </div>
        <div className="shrink-0 text-left">
          <div className="text-2xl font-black leading-none tracking-tight" dir="ltr">{shekel(s.price)}</div>
          <div className="mt-1 text-[11px] text-[var(--cin-muted)]">שולם, כולל מע״מ</div>
        </div>
      </div>
    </article>
  );
}

export function ShippingNote({ onDark = false }: { onDark?: boolean }) {
  return (
    <div className={`flex items-start gap-3 rounded-2xl px-5 py-4 ${onDark ? 'bg-white/[0.07] text-white ring-1 ring-white/10' : 'bg-[var(--cin-paper-soft)] text-[var(--cin-ink)] ring-1 ring-[var(--cin-line)]'}`}>
      <Truck className={`mt-0.5 h-6 w-6 shrink-0 ${onDark ? 'text-[#A9BEFF]' : 'text-[var(--cin-blue)]'}`} aria-hidden="true" />
      <p className="text-[15px] leading-relaxed">
        <strong className="font-bold">משלוח עם UPS לכל הארץ.</strong>{' '}
        <span className={onDark ? 'text-white/75' : 'text-[var(--cin-muted)]'}>חינם בהזמנה מעל ₪2,000, ואפשר גם לאסוף מהמעבדה בנטור.</span>
      </p>
    </div>
  );
}

export default function RecentSales({ from = 'home', limit = 6 }: { from?: string; limit?: number }) {
  const sales = [...data.sales].sort((a, b) => b.date.localeCompare(a.date)).slice(0, limit);
  if (sales.length === 0) return null;
  return (
    <section id="sold" aria-labelledby="sold-title" className="cin-dark font-heebo scroll-mt-16 relative isolate overflow-hidden bg-[#0B1224] py-[clamp(56px,8vw,112px)] px-[clamp(20px,5vw,80px)] text-white">
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-[radial-gradient(800px_420px_at_10%_0%,rgba(0,212,170,0.16),transparent_60%),radial-gradient(760px_480px_at_95%_100%,rgba(30,85,217,0.35),transparent_62%)]"
      />
      <div className="mx-auto max-w-[1280px]">
        <div className="cin-reveal mb-10 grid items-end gap-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3.5 py-1.5 text-[13px] font-medium text-white/85">
              <span className="relative flex h-2 w-2" aria-hidden="true">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:hidden" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              נמכר לאחרונה
            </div>
            <h2 id="sold-title" className="cin-display-sm text-white">
              מחשבים שיצאו מהמעבדה,
              <br />
              <span className="text-[#A9BEFF]">ובכמה הם נמכרו.</span>
            </h2>
          </div>
          <p className="text-lg leading-relaxed text-white/75 lg:col-span-5">
            כל מחשב כאן התחיל בהצעת מחיר אישית בוואטסאפ, ויצא מוכן לעבודה: מותקן, מעודכן ובדוק. המחיר הוא מה
            שהלקוח שילם בפועל. מוצגים רק שם פרטי וישוב.
          </p>
        </div>

        {/* Phones: a swipeable row (cards ~86% wide, snapping); desktop: a 3-column grid. */}
        <ul
          tabIndex={0}
          aria-label="מחשבים שנמכרו לאחרונה — אפשר לגלול הצידה"
          className="-mx-5 mb-8 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 outline-none [scrollbar-width:none] focus-visible:ring-2 focus-visible:ring-[#A9BEFF] md:mx-0 md:grid md:grid-cols-2 md:gap-5 md:overflow-visible md:px-0 lg:grid-cols-3 [&::-webkit-scrollbar]:hidden"
        >
          {sales.map((s, i) => (
            <li key={s.id} className={`cin-reveal cin-reveal-d${(i % 3) + 1} w-[86%] shrink-0 snap-center md:w-auto`}>
              <SaleCard s={s} />
            </li>
          ))}
        </ul>

        <div className="cin-reveal grid items-center gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
          <ShippingNote onDark />
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
            <a href={WA} target="_blank" rel="noopener noreferrer" data-track-from={`${from}-sold`} className="cin-btn-primary cin-btn-on-dark cin-magnetic justify-center gap-2">
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
              רוצים הצעה כזאת? כתבו לי
            </a>
            <Link to="/shop" className="cin-btn-ghost self-center text-white/85">
              לקטלוג המחשבים ←
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
